import React, { useState } from 'react';
import { Upload, Loader2, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { ICON_NAMES, getIcon } from '../../content/icons';
import { uploadImage } from '../../admin/api';

export function Text({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-slate-900">{label}</span>}
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="form-input" />
    </label>
  );
}

export function Area({ label, value, onChange, placeholder, rows = 3 }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-slate-900">{label}</span>}
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="form-input" />
    </label>
  );
}

export function Color({ label, value, onChange }) {
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-slate-900">{label}</span>}
      <div className="flex items-center gap-2">
        <input type="color" value={value || '#2563eb'} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-gray-200" />
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} className="form-input flex-1 text-sm" />
      </div>
    </label>
  );
}

export function IconSelect({ label, value, onChange }) {
  const Icon = getIcon(value);
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-sm font-medium text-slate-900">{label}</span>}
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50">
          <Icon className="h-5 w-5 text-blue-600" />
        </span>
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="form-input cursor-pointer">
          {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </label>
  );
}

export function ImageInput({ label, value, onChange, fallback }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [meta, setMeta] = useState(null);
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const previewUrl = URL.createObjectURL(file);
      const probe = new Image();
      probe.onload = () => { setMeta({ w: probe.naturalWidth, h: probe.naturalHeight }); URL.revokeObjectURL(previewUrl); };
      probe.onerror = () => URL.revokeObjectURL(previewUrl);
      probe.src = previewUrl;
      const { url } = await uploadImage(file);
      onChange(url);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      {label && <span className="mb-1.5 block text-sm font-medium text-slate-900">{label}</span>}
      <div
        className="relative flex aspect-[16/9] items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white"
        style={fallback ? { backgroundImage: `linear-gradient(135deg, ${fallback[0]}, ${fallback[1]})` } : undefined}
      >
        {value && <img src={value} alt="" className="absolute inset-0 h-full w-full object-contain p-2" onLoad={(e) => setMeta({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        <label className="relative z-10 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-gray-800 shadow hover:bg-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? '上传中…' : '上传图片'}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
        </label>
      </div>
      {meta && <p className="mt-1 text-xs text-gray-500">{'\u539f\u56fe\u5c3a\u5bf8'}?{meta.w} ? {meta.h}</p>}
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="或填图片地址，如 /images/x.jpg" className="form-input mt-2 text-xs" />
      {err && <p className="mt-1 text-xs text-red-500">{err}</p>}
    </div>
  );
}

// 通用列表编辑器：增删改 + 上下移动
export function ListEditor({ items = [], onChange, newItem, render, addLabel = '新增一项' }) {
  const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, newItem()]);

  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={it.id != null ? it.id : i} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500">第 {i + 1} 项</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 disabled:opacity-30 cursor-pointer"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-200 disabled:opacity-30 cursor-pointer"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => remove(i)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 cursor-pointer"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          {render(it, (patch) => update(i, patch), i)}
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:border-blue-300 hover:text-blue-600 cursor-pointer">
        <Plus className="h-4 w-4" /> {addLabel}
      </button>
    </div>
  );
}